import { useState } from "react";
import { useIntl, defineMessages } from "react-intl";

import { HookFormPassword } from "common/account/password/password";
import { StyledTextInput } from "common/core/form/text";
import { useForm } from "common/core/form";
import { defaultRequiredMessage } from "common/core/form/error";
import { useA11y } from "common/accessibility";
import { useSelector } from "redux/util";

import { Continue, ForgotPasswordLink, LoginHeader, BackButton } from "./components";
import Styles from "./index.module.scss";

export type FormValues = {
  password: string;
};

type Props = {
  onPasswordSubmit: (values: FormValues) => Promise<unknown>;
  onBack: () => void;
  setForgotPasswordScreen: () => void;
  password: string;
  email: string;
  emailReminder: boolean;
};

const MESSAGES = defineMessages({
  emailLabel: {
    id: "59d9a2da-5d4a-43d6-9a3f-086e8e1e0bed",
    defaultMessage: "Email address",
  },
  passwordLabel: {
    id: "b9135b5a-18db-44ea-a2c8-6546ef478cb5",
    defaultMessage: "Password",
  },
  backButtonAriaLabel: {
    id: "139642bd-0c27-4190-aaf8-0d6e3ec4842c",
    defaultMessage: "Back to email input",
  },
  invalidError: {
    id: "3170e3fa-f1a1-4bf3-a57e-55585e4beee0",
    defaultMessage: "Email or password is invalid",
  },
});

export default function PasswordScreen({
  email,
  emailReminder,
  onPasswordSubmit,
  onBack,
  setForgotPasswordScreen,
  password,
}: Props) {
  const intl = useIntl();
  const [loginLocked, setLoginLocked] = useState(false);
  const form = useForm<FormValues>({ mode: "all", defaultValues: { password: password || "" } });
  const { handleSubmit, register, formState, setFocus, setError } = form;
  const { errors } = formState;
  const errorId = useA11y().useRegisteredId("password");
  const authenticationError = useSelector((state) => state.authentication).error;

  const submitHandler = (values: FormValues) => {
    if (loginLocked) {
      return;
    }
    setLoginLocked(true);
    return onPasswordSubmit(values).catch(() => {
      // We expect a success to redirect us. We don't need or want to unlock.
      setLoginLocked(false);
      setFocus("password");
      setError("password", {
        type: authenticationError.type,
        message: intl.formatMessage(MESSAGES.invalidError),
      });
    });
  };

  return (
    <>
      <LoginHeader />
      <form className={Styles.form} onSubmit={handleSubmit(submitHandler)}>
        {emailReminder && (
          <StyledTextInput
            disabled
            aria-invalid="false"
            value={email}
            className={Styles.email}
            label={intl.formatMessage(MESSAGES.emailLabel)}
            placeholderAsLabel
          />
        )}
        <HookFormPassword
          setFocus={setFocus}
          inputClass={Styles.input}
          label={intl.formatMessage(MESSAGES.passwordLabel)}
          registerProps={register("password", { required: defaultRequiredMessage(intl) })}
          invalid={Boolean(errors.password)}
          error={errors.password}
          aria-describedby={authenticationError.error ? errorId : undefined}
        />
        <Continue disabled={!formState.isValid || loginLocked || Boolean(errors.password)} />
      </form>
      <ForgotPasswordLink setForgotPasswordScreen={setForgotPasswordScreen} />
      <BackButton onBack={onBack} aria-label={intl.formatMessage(MESSAGES.backButtonAriaLabel)} />
    </>
  );
}
